/**
 * The cards pinned where the person is looking, as the chat context carries
 * them.
 *
 * A layout lives on this phone and nowhere else, so the server can only know
 * which card "the This month card" is if the phone says. This reads the same
 * layouts the strips draw — the record's own when the sheet was opened inside
 * a record, and the Dashboard's always — and hands each card over with its
 * label, one sentence saying what it draws, and the layout id the agent's
 * proposal will carry back unchanged. No number is computed: the agent is told
 * what each card asks, never what it answers.
 */
import type { AgentScreenCard, RecordSchemaResponse } from '@alluzo/shared';

import { getBusinessInsightLayout, getInsightLayout, listCatalog, schemaFor } from '@/lib/entry-repo';
import { hopSchemasOf } from '@/lib/hop-schemas';
import { capabilitiesOf, defaultBusinessLayoutFor } from '@/lib/insight-business-candidates';
import { businessLabelFor, describeBusinessInsight, type BusinessPresentContext } from '@/lib/insight-business-present';
import { isHostedSpec } from '@/lib/insight-business-query';
import { defaultLayoutFor } from '@/lib/insight-layout';
import { describeInsight, labelFor } from '@/lib/insight-present';

/** The record's own strip: what `useInsights` would draw, without drawing it. */
const recordCards = (recordId: string, schema: RecordSchemaResponse): AgentScreenCard[] => {
  const layout = getInsightLayout(recordId) ?? defaultLayoutFor(schema);
  const targets = hopSchemasOf(schema);
  return layout.map((entry) => ({
    id: entry.id,
    label: labelFor(schema, entry, {}, targets),
    says: describeInsight(schema, entry.spec, {}, targets),
    card: entry.card,
    placement: 'record',
    recordId,
  }));
};

/** The Dashboard's strip. A hosted card names the record it is asked of. */
const dashboardCards = (tenantId: string): AgentScreenCard[] => {
  const catalog = listCatalog(tenantId);
  const capabilities = capabilitiesOf(catalog);
  const layout = getBusinessInsightLayout(tenantId) ?? defaultBusinessLayoutFor(capabilities);
  const present: BusinessPresentContext = {
    currency: capabilities.currency,
    recordNames: Object.fromEntries(catalog.map((row) => [row.id, row.name])),
    meId: null,
    schemaOf: schemaFor,
  };
  return layout.map((entry) => ({
    id: entry.id,
    label: businessLabelFor(present, entry),
    says: describeBusinessInsight(present, entry.spec),
    card: entry.card,
    placement: 'dashboard',
    ...(isHostedSpec(entry.spec) ? { recordId: entry.spec.host } : {}),
  }));
};

/**
 * Every card the agent may be asked about this turn. Reads the mirror in the
 * same tick; a record whose schema has not been cached contributes nothing,
 * and a throw anywhere leaves the list empty rather than the turn unsent.
 */
export const screenCardsFor = (tenantId: string, recordId: string | null): AgentScreenCard[] => {
  try {
    const schema = recordId === null ? null : schemaFor(recordId);
    const own = schema === null || recordId === null ? [] : recordCards(recordId, schema);
    return [...own, ...dashboardCards(tenantId)];
  } catch {
    return [];
  }
};
